/**
 * Состояние ленты: сырые вилки от сервера, фильтры пользователя, пауза и скрытия.
 *
 * Три решения, из-за которых это отдельный модуль с тестами:
 *
 * 1. **Пауза под курсором.** Лента переставляется на каждой дельте. Пользователь,
 *    наведший курсор на вилку, чтобы скопировать сумму, обязан видеть ту же строку
 *    через секунду. Поэтому видимый список замораживается, а изменения копятся и
 *    показываются счётчиком — это ровно то поведение, которого не хватает
 *    конкуренту (ленту приходится ловить).
 * 2. **Скрытие со счётчиком, а не удаление.** Скрытая вилка остаётся в состоянии:
 *    иначе она вернётся в ленту при следующей дельте, и человек будет прятать её
 *    заново каждые пять секунд.
 * 3. **Фильтры — чистая функция.** Их проверяют тесты без React и без сокета:
 *    ошибка в фильтре означает либо пропущенную вилку (потерянные деньги), либо
 *    показанную мусорную (потерянные деньги), и оба случая молчаливы.
 */

import { create } from 'zustand';

import {
  type Fork,
  type Middle,
  ageOf,
  marketPrefix,
  roiValue,
  sourcesOf,
} from '../lib/protocol';
import { fitsAtLeast } from '../lib/volume';
import type { FeedStatus } from '../ws/client';

export type SortKey = 'roi' | 'age' | 'sv';

export interface Filters {
  /** Минимальный NET-ROI в процентах. */
  minRoi: number;
  /** Максимальный возраст в секундах. 0 — без ограничения. */
  maxAge: number;
  /** Пустой набор — все дисциплины. */
  sports: string[];
  /** Пустой набор — все площадки. */
  sources: string[];
  /**
   * Требовать, чтобы В СПИСКЕ площадок были ОБЕ ноги, а не любая.
   *
   * Аккаунты у человека в трёх конторах. Вилка, где вторая нога в четвёртой, —
   * не вилка, а строка мусора, которую он всё равно пролистает; но пролистывать
   * её надо глазами, а это те самые секунды, за которые вилка умирает.
   */
  bothLegs: boolean;
  /** Пустой набор — все семейства рынков (WIN, TTL, HCP, ITTL). */
  markets: string[];
  /** Минимальный объём, который должен влезать в плюс. 0 — без ограничения. */
  minVol: number;
  /** Показывать вилки с пометками анти-скама. */
  showFlagged: boolean;
  sort: SortKey;
}

export const DEFAULT_FILTERS: Filters = {
  minRoi: 0,
  maxAge: 0,
  sports: [],
  sources: [],
  bothLegs: false,
  markets: [],
  minVol: 0,
  // По умолчанию помеченные видны: скрыть их значило бы решить за пользователя,
  // что вилка с микролимитом ему не нужна. Пометка объясняет, выбор за ним.
  showFlagged: true,
  sort: 'roi',
};

export interface SourceState {
  code: string;
  stale: boolean;
  quotes: number;
  /**
   * Сколько секунд назад площадка обновляла котировки. undefined — сервер
   * старый и этого не сказал.
   *
   * «На связи» бинарным флагом — полуправда: источник с лагом 9 секунд при
   * пороге 15 выглядит так же, как источник с лагом 0.2, а ставить по нему уже
   * поздно. Порог у каждого источника свой, поэтому число без него бессмысленно.
   */
  lagSec?: number | null;
  staleAfterSec?: number;
}

export interface FeedState {
  status: FeedStatus;
  /** Состояние источников из /v1/meta. Пустой список — движок не сканирует. */
  sources: SourceState[];
  /**
   * Медиана сквозной задержки «котировка → кадр», миллисекунды. null — выборки
   * ещё не набралось, и тогда на экране нет ничего: подставить ноль значило бы
   * обещать мгновенность, а красивое число вместо замера — это то, за что мы
   * ругаем конкурента.
   */
  latencyMs: number | null;
  /** Всё, что пришло с сервера, ключ — id вилки. */
  forks: Map<string, Fork>;
  /** Активных вилок на сервере: может быть больше, чем в снапшоте (потолок кадра). */
  activeForks: number;
  /** Обрезано потолком снапшота. Молчать об этом нельзя: лента выглядела бы полной. */
  truncated: number;
  /**
   * Полувилки. Отдельным списком, а не вперемешку с вилками: прибыль в них не
   * гарантирована, и одна общая лента означала бы, что человек читает две ставки
   * с разным смыслом по одним и тем же правилам.
   *
   * Приезжают снапшотом по REST, а не дельтой: жанр живёт минутами, а не
   * секундами (окно между линиями не съедается так, как съедается вилка), и
   * второй дельта-канал ради него был бы платой сложностью без выигрыша.
   */
  middles: Middle[];
  hidden: string[];
  /** Заморозка видимого списка: курсор в ленте. */
  paused: boolean;
  /** Ревизия состояния — растёт на каждом сообщении сервера. */
  revision: number;
  /** Ревизия на момент заморозки: разница — счётчик пропущенных изменений. */
  pausedAt: number;
  frozen: Fork[] | null;
  filters: Filters;
  /**
   * Пищать при новой вилке. Выключено по умолчанию: звук, включённый без
   * спроса, выключают вместе со вкладкой.
   */
  sound: boolean;
  /** Порог ROI для звука: пищать на каждой вилке в 0.1% — то же, что не пищать. */
  soundRoi: number;
  /** Легенда правил анти-скама с сервера: код → что правило значит вообще. */
  ruleCatalog: Record<string, string>;
  /** Банк для калькулятора и лестницы объёма. */
  bank: number;
  expanded: string | null;

  apply: (forks: Map<string, Fork>, meta: Record<string, unknown>) => void;
  setStatus: (status: FeedStatus) => void;
  setSources: (sources: SourceState[]) => void;
  setLatency: (latencyMs: number | null) => void;
  setMiddles: (middles: Middle[]) => void;
  setFilters: (patch: Partial<Filters>) => void;
  setBank: (bank: number) => void;
  setPaused: (paused: boolean) => void;
  hide: (id: string) => void;
  unhideAll: () => void;
  toggleExpanded: (id: string) => void;
  setSound: (sound: boolean) => void;
  setSoundRoi: (roi: number) => void;
  setRuleCatalog: (catalog: Record<string, string>) => void;
}

const asCount = (value: unknown): number => (typeof value === 'number' ? value : 0);

export const useFeed = create<FeedState>((set, get) => ({
  status: 'connecting',
  sources: [],
  latencyMs: null,
  forks: new Map(),
  activeForks: 0,
  truncated: 0,
  middles: [],
  hidden: [],
  paused: false,
  revision: 0,
  pausedAt: 0,
  frozen: null,
  filters: DEFAULT_FILTERS,
  sound: false,
  soundRoi: 2,
  ruleCatalog: {},
  bank: 100,
  expanded: null,

  apply: (forks, meta) =>
    set((state) => ({
      // Копия обязательна: клиент держит ту же Map и мутирует её на каждой дельте,
      // а React сравнивает по ссылке и иначе не перерисует ленту.
      forks: new Map(forks),
      activeForks: asCount(meta.activeForks) || forks.size,
      truncated: asCount(meta.truncated),
      revision: state.revision + 1,
      // Скрытия чистим от исчезнувших вилок: список не должен расти вечно.
      hidden: state.hidden.filter((id) => forks.has(id)),
    })),

  setStatus: (status) => set({ status }),
  setSources: (sources) => set({ sources }),
  setLatency: (latencyMs) => set({ latencyMs }),
  // Момент получения ставится здесь по той же причине, что и у вилок: сервер
  // присылает возраст один раз, дальше время идёт локально.
  setMiddles: (middles) =>
    set({ middles: middles.map((row) => ({ ...row, at: Date.now() })) }),
  setFilters: (patch) => set((state) => ({ filters: { ...state.filters, ...patch } })),
  setBank: (bank) => set({ bank: Number.isFinite(bank) && bank > 0 ? bank : 1 }),

  setPaused: (paused) => {
    const state = get();
    if (paused === state.paused) return;
    set(
      paused
        ? { paused: true, pausedAt: state.revision, frozen: visibleForks(state) }
        : { paused: false, frozen: null },
    );
  },

  hide: (id) =>
    set((state) => ({
      hidden: state.hidden.includes(id) ? state.hidden : [...state.hidden, id],
      frozen: state.frozen === null ? null : state.frozen.filter((fork) => fork.id !== id),
      expanded: state.expanded === id ? null : state.expanded,
    })),

  unhideAll: () => set({ hidden: [] }),
  toggleExpanded: (id) => set((state) => ({ expanded: state.expanded === id ? null : id })),
  setSound: (sound) => set({ sound }),
  setSoundRoi: (roi) => set({ soundRoi: Number.isFinite(roi) ? roi : 0 }),
  setRuleCatalog: (ruleCatalog) => set({ ruleCatalog }),
}));

/**
 * Производные ленты — функции от срезов состояния, а не селекторы стора.
 *
 * Причина техническая и однажды уже стоившая работающего сайта: zustand 5 отдаёт
 * результат селектора в `useSyncExternalStore` без мемоизации. Селектор, который
 * собирает новый массив или объект, для React выглядит вечно меняющимся состоянием —
 * рендер уходит в бесконечный цикл, и вместо ленты остаётся пустая страница. Причём
 * пустая молча: сервер отдаёт 200, бандл на месте, проверки деплоя зелёные.
 *
 * Поэтому компоненты подписываются только на ссылки, которые лежат в состоянии
 * (`forks`, `filters`, `hidden`, `frozen`, `sources`), а списки и фасеты считают у
 * себя в `useMemo`. Узкие типы срезов ниже — чтобы это нельзя было забыть: функции
 * не требуют всего состояния и явно называют, от чего зависят.
 */
type ListSlice = Pick<FeedState, 'forks' | 'filters' | 'hidden'>;
export type RenderSlice = ListSlice & Pick<FeedState, 'frozen'>;

/** Сколько изменений лента накопила, пока стояла на паузе. */
export const pendingChanges = (state: Pick<FeedState, 'paused' | 'revision' | 'pausedAt'>): number =>
  state.paused ? state.revision - state.pausedAt : 0;

export type Health =
  | { kind: 'ok' }
  | { kind: 'engine_off' }
  | { kind: 'silent'; sources: string[] };

/**
 * Честное состояние ленты, а не «вилок нет».
 *
 * Пустая лента бывает по трём совершенно разным причинам, и путать их нельзя:
 * настоящих вилок правда нет (норма), движок не сканирует вовсе, источники молчат.
 * Второе и третье выглядят на экране одинаково с первым — и именно поэтому опаснее
 * падения: человек сидит перед пустым сканером и думает, что рынок спокоен.
 */
export function health(state: Pick<FeedState, 'sources'>): Health {
  if (state.sources.length === 0) return { kind: 'engine_off' };
  const silent = state.sources.filter((source) => source.stale).map((source) => source.code);
  return silent.length === state.sources.length ? { kind: 'silent', sources: silent } : { kind: 'ok' };
}

export interface SourceView {
  text: string;
  mood: 'ok' | 'lagging' | 'silent';
  hint: string;
}

/** Секунды человеку: 0.4 с видно как 0.4, 41.2 с — как 41. */
const secs = (value: number): string => (value >= 10 ? String(Math.round(value)) : value.toFixed(1));

/**
 * Состояние источника словом и числом, а не бинарным «на связи».
 *
 * Между «отдаёт цены сейчас» и «протух» лежит самое опасное состояние: площадка
 * ещё считается живой, но её котировке восемь секунд при пороге пятнадцать. Вилка
 * по такой цене выглядит настоящей и не берётся — ставку просто не примут.
 * Порог у каждого источника свой (сокет и медленный REST-опрос несравнимы),
 * поэтому «отстал» считается долей от его собственного порога, а не константой.
 */
export function sourceView(source: SourceState): SourceView {
  const lag = typeof source.lagSec === 'number' ? source.lagSec : null;
  const limit = source.staleAfterSec;
  const limitHint = limit === undefined ? '' : `, порог ${secs(limit)} с`;
  if (source.stale) {
    return {
      mood: 'silent',
      text: lag === null ? 'молчит' : `молчит ${secs(lag)} с`,
      hint: `по этому источнику ставить нельзя: котировки не обновляются${limitHint}`,
    };
  }
  if (lag === null) return { mood: 'ok', text: 'на связи', hint: 'сервер не сообщил задержку' };
  const lagging = limit !== undefined && lag > limit / 2;
  return {
    mood: lagging ? 'lagging' : 'ok',
    text: `${secs(lag)} с`,
    hint: lagging
      ? `котировка уже немолодая: ${secs(lag)} с${limitHint}. линию стоит сверить руками`
      : `последнее обновление котировок ${secs(lag)} с назад${limitHint}`,
  };
}

/**
 * Видимый список: фильтры, скрытия, сортировка.
 *
 * `now` инъекционный ради тестов: фильтр по возрасту, проверенный на `Date.now()`,
 * зелёный до полуночи и мигающий после.
 */
export function visibleForks(state: ListSlice, now: number = Date.now()): Fork[] {
  const { filters, hidden } = state;
  const sports = new Set(filters.sports);
  const sources = new Set(filters.sources);
  const markets = new Set(filters.markets);

  const picked = [...state.forks.values()].filter((fork) => {
    if (hidden.includes(fork.id)) return false;
    if (roiValue(fork) < filters.minRoi) return false;
    if (filters.maxAge > 0 && ageOf(fork, now) > filters.maxAge) return false;
    if (sports.size > 0 && !sports.has(fork.sport ?? '')) return false;
    if (markets.size > 0 && !markets.has(marketPrefix(fork.market))) return false;
    if (sources.size > 0 && !matchesSources(fork, sources, filters.bothLegs)) return false;
    // Порог объёма — про деньги, а не про удобство: вилка с ROI 22% и стаканом
    // на 13 долларов занимает ту же строку, что и та, куда влезает банк.
    if (!fitsAtLeast(fork, filters.minVol)) return false;
    if (!filters.showFlagged && fork.st === 'flagged') return false;
    return true;
  });

  // Вторичный ключ — id: при равном ключе порядок обязан быть устойчивым, иначе
  // строки прыгают местами на каждой дельте без причины.
  picked.sort((a, b) => sortKey(a, b, filters.sort, now) || a.id.localeCompare(b.id));
  return picked;
}

/**
 * Площадки вилки против выбора пользователя.
 *
 * Вилка без ног не проходит фильтр «обе мои» намеренно: пустой список ног
 * формально удовлетворяет «все ноги в наборе», и такая вилка всплыла бы в самом
 * строгом режиме — ровно там, где человек просил показывать только своё.
 */
function matchesSources(fork: Fork, chosen: Set<string>, bothLegs: boolean): boolean {
  const legs = sourcesOf(fork);
  return bothLegs
    ? legs.length > 0 && legs.every((code) => chosen.has(code))
    : legs.some((code) => chosen.has(code));
}

/** Живучесть числом. Без выборки — в самый конец списка, а не в начало. */
const survivalOf = (fork: Fork): number => (typeof fork.sv === 'number' ? fork.sv : -1);

function sortKey(a: Fork, b: Fork, sort: SortKey, now: number): number {
  if (sort === 'age') return ageOf(a, now) - ageOf(b, now);
  // Живучесть по убыванию: вилка, которая обычно держится минуту, даёт время
  // дойти до второй конторы; та, что живёт семь секунд, — не даёт.
  if (sort === 'sv') return survivalOf(b) - survivalOf(a);
  return roiValue(b) - roiValue(a);
}

/** Список для отрисовки: на паузе — замороженный, иначе живой. */
export const renderedForks = (state: RenderSlice, now?: number): Fork[] =>
  state.frozen ?? visibleForks(state, now);

/**
 * Строки одного матча, сложенные в группу.
 *
 * `head` — лучшая вилка матча, `rest` — остальные. Порядок групп задаёт `head`,
 * то есть тот же порядок, что был бы у плоского списка: свёртка меняет плотность
 * ленты, но не решение, что показать первым.
 */
export interface EventGroup {
  key: string;
  head: Fork;
  rest: Fork[];
}

/**
 * Свернуть ленту по событиям.
 *
 * Зачем. Пока карточный рынок не работал, на матч приходилась одна строка, и
 * группировать было нечего. Как только заработает, на одном матче появятся форы
 * и тоталы по каждой карте — десятки строк, из которых человеку интересна лучшая,
 * а остальные нужны только чтобы понять, что выбор есть. Плоский список в этот
 * момент превращается в стену, где один популярный матч вытесняет с экрана все
 * прочие: лента становится нечитаемой ровно тогда, когда наконец наполняется.
 *
 * Порядок внутри группы сохраняется входной: список уже отсортирован по выбору
 * пользователя, и пересортировывать его здесь значило бы тихо отменить этот выбор.
 *
 * Вилка без отпечатка события (старый сервер, откат) остаётся сама по себе —
 * группа из одной строки. Склеивать такие по общему пустому ключу нельзя: это
 * собрало бы разные матчи в одну кучу, то есть показало бы одно вместо другого.
 */
export function groupByEvent(forks: Fork[]): EventGroup[] {
  const groups: EventGroup[] = [];
  const index = new Map<string, EventGroup>();
  for (const fork of forks) {
    const key = typeof fork.ev === 'string' ? fork.ev : '';
    const known = key === '' ? undefined : index.get(key);
    if (known === undefined) {
      const group: EventGroup = { key: key || fork.id, head: fork, rest: [] };
      if (key !== '') index.set(key, group);
      groups.push(group);
      continue;
    }
    known.rest.push(fork);
  }
  return groups;
}

/** Дисциплины и площадки, которые реально есть в ленте, — для панели фильтров. */
export function facets(state: Pick<FeedState, 'forks'>): { sports: string[]; sources: string[] } {
  const sports = new Set<string>();
  const sources = new Set<string>();
  for (const fork of state.forks.values()) {
    if (fork.sport) sports.add(fork.sport);
    for (const code of sourcesOf(fork)) sources.add(code);
  }
  return { sports: [...sports].sort(), sources: [...sources].sort() };
}
